import { useState, useRef } from "react";
import { MoreVertical, Info, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useClickOutside from "../../hooks/useClickOutside";

/**
 * TileCard — shared design-language card for tile grids across Spectra, Apps, Forge etc.
 *
 * Props:
 *   avatar      — React node (logo, initials circle, emoji)
 *   title       — string
 *   tag         — string (optional, renders as compact tag)
 *   description — string (optional, shown on front face, line-clamp-2)
 *   status      — React node (optional, badge/dot shown top-right)
 *   active      — bool (optional, green presence dot on avatar bottom-right)
 *   menu        — array of { label, onClick, destructive? } (optional, ⋮ top-right)
 *   onCardClick — fn (optional, makes whole front face clickable)
 *   backContent — React node (optional; if provided, ⓘ button appears)
 *   disabled    — bool (grayscale + reduced opacity)
 *   className   — string (extra classes for outer wrapper)
 */
const TileCard = ({
  avatar,
  title,
  tag,
  description,
  status,
  active,
  menu,
  onCardClick,
  backContent,
  disabled = false,
  className = "",
}) => {
  const [flipped, setFlipped] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useClickOutside(menuRef, () => setMenuOpen(false));

  const hasMenu = menu && menu.length > 0;
  const hasBack = !!backContent;

  return (
    <div
      className={`photon-content-card relative rounded-2xl bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border shadow-sm hover:shadow-md transition-shadow duration-200 min-h-[130px] ${disabled ? "opacity-60 grayscale" : ""} ${className}`}
      style={{ perspective: "1000px" }}
    >
      {/* ── 3D flip container ───────────────────────────────────────────── */}
      <div
        className={`relative w-full h-full transition-transform duration-500`}
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          minHeight: "130px",
        }}
      >
        {/* ── FRONT FACE ──────────────────────────────────────────────── */}
        <div
          className="absolute inset-0 p-4 flex flex-col"
          style={{ backfaceVisibility: "hidden" }}
        >
          {/* Top row: avatar + title + status/menu */}
          <div className="flex items-start justify-between gap-2">
            <div
              className={`flex items-center gap-2.5 flex-1 min-w-0 ${onCardClick && !disabled ? "cursor-pointer" : ""}`}
              onClick={onCardClick && !disabled ? onCardClick : undefined}
            >
              {/* Avatar */}
              <div className="flex-shrink-0">{avatar}</div>
              {/* Title */}
              <span className="text-sm font-semibold text-ink-ink dark:text-ink-white truncate leading-tight">
                {title}
              </span>
            </div>

            {/* Right controls: active dot + status badge + menu */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {active && (
                <span className="block w-2.5 h-2.5 rounded-full bg-fill-emerald-400 ring-2 ring-surface-card" title="Active" />
              )}
              {status && <div>{status}</div>}
              {hasMenu && (
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((v) => !v);
                    }}
                    className="p-1 rounded-md text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-border transition-colors"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                  <AnimatePresence>
                    {menuOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -4 }}
                        transition={{ duration: 0.1 }}
                        className="absolute right-0 mt-1 w-36 bg-surface-dialog rounded-xl shadow-lg border border-edge-gray-200 dark:border-edge-panel-border py-1 z-50"
                      >
                        {menu.map((item, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpen(false);
                              item.onClick();
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                              item.destructive
                                ? "text-ink-red-600 dark:text-ink-red-300 hover:bg-fill-red-100 dark:hover:bg-fill-red-600/10"
                                : "text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-border"
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Tag */}
          {tag && (
            <div
              className={`mt-2 ${hasBack && !description ? "pr-6" : ""} ${onCardClick && !disabled ? "cursor-pointer" : ""}`}
              onClick={onCardClick && !disabled ? onCardClick : undefined}
            >
              <span className="photon-accent-label photon-reference-tag inline-block max-w-full truncate px-2 py-0.5 rounded text-[10px] font-medium bg-fill-indigo-100 text-ink-indigo-800 dark:bg-fill-hex-312e81/60 dark:text-ink-hex-a5b4fc">
                {tag}
              </span>
            </div>
          )}

          {/* Description */}
          {description && (
            <p
              className={`mt-2 text-[11px] text-ink-gray-500 dark:text-ink-gray-400 line-clamp-2 leading-relaxed flex-1 ${hasBack ? "pr-6" : ""} ${onCardClick && !disabled ? "cursor-pointer" : ""}`}
              onClick={onCardClick && !disabled ? onCardClick : undefined}
            >
              {description}
            </p>
          )}

          {/* ⓘ flip button — bottom right */}
          {hasBack && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFlipped(true);
              }}
              className="absolute bottom-2 right-2 text-ink-gray-400 hover:text-ink-primary transition-colors"
              title="View details"
            >
              <Info className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* ── BACK FACE ───────────────────────────────────────────────── */}
        {hasBack && (
          <div
            className="absolute inset-0 p-4 flex flex-col rounded-2xl bg-surface-card"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            {backContent}
            {/* ‹‹ flip back — same position as ⓘ */}
            <button
              type="button"
              onClick={() => setFlipped(false)}
              className="absolute bottom-2 right-2 text-ink-gray-400 hover:text-ink-primary transition-colors"
              title="Back"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TileCard;
